// Shared constants for the Crude Planning module.
//
// The whole module currently runs on an in-memory dummy layer (no backend yet),
// so anything that would normally be a server enum lives here as a plain
// constant. When the real API lands, most of these map 1:1 to backend enums.

// Fixed procurement type — the Create popup pre-fills this and it is read-only.
export const PROCUREMENT_TYPE = 'Hydrocarbon Crude & Condensate';

// Status shown in the "Status Planning" column.
//
// ⚠️ Despite the column name this is `Procurement.PhaseStatus` — the header's
// status, NOT `Planning.PhaseStatusId` (contract section 2, pinned down
// 2026-07-23). Planning's own status only ever holds Draft or Submitted, but the
// header follows the tender: once it reaches a later stage this field reports
// that stage's status instead, e.g. 'On Hard Recall' from Tender Opening while
// Planning itself is back in Draft and fully editable. HARD_RECALL is listed
// here for exactly that case. The real signal for recall state is the
// `isHardRecall` flag — never this text.
export const STATUS = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  HARD_RECALL: 'On Hard Recall',
};

// Main Status — `Procurement.Phase`, i.e. the stage the tender as a whole has
// reached. Submit moves it Planning -> Procurement, and Tender Opening takes it
// further still; a Hard Recall reopens Planning WITHOUT moving the header back,
// so 'Tender Opening' is a perfectly normal value on a row you can edit.
export const MAIN_STATUS = {
  PLANNING: 'Planning',
  PROCUREMENT: 'Procurement',
  TENDER_OPENING: 'Tender Opening',
};

// Jenis Pengadaan filter for master Klausa Information (Clause Detail source).
export const JENIS_PENGADAAN_COS = 'COS';

// Procurement method (mekanisme pengadaan) dropdown options.
export const PROCUREMENT_METHODS = [
  'Pelelangan Khusus',
  'Pemilihan Langsung',
  'Tunjuk Langsung',
];

// Contract type cards on the Requirement > Contract section. `value` is the raw
// API enum (`ContractType`: Term | Spot) sent as-is on SaveMainRequirement; the
// label is what the user sees (rendered upper-cased, so "Spot" shows as "SPOT").
export const CONTRACT_TYPES = [
  { value: 'Term', label: 'Term' },
  { value: 'Spot', label: 'Spot' },
];

// Incoterms — multi-select checkboxes on the Requirement > Cargo Terms card.
export const INCOTERMS = [
  { value: 'FOB', label: 'FOB (Free on Board)' },
  { value: 'DAP', label: 'DAP (Delivery at Place)' },
  { value: 'CFR', label: 'CFR (Cost and Freight)' },
];

// Overview steps. Each maps to a tab and can be In Progress or Completed.
export const STEP = {
  REQUIREMENT: 'requirement',
  SUPPORTING_DOCUMENT: 'supportingDocument',
  DMUT: 'dmut',
};

export const STEP_STATUS = {
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
};

// Raw wire values for `PlanningStepStatus` (planningApi maps STEP_STATUS <->
// these). The C# member names have no space, unlike the display labels above.
export const STEP_STATUS_API = {
  IN_PROGRESS: 'InProgress',
  COMPLETED: 'Completed',
};

// Document source labels shown in the Supporting Document list.
export const DOC_SOURCE = {
  MASTER: 'Master Document',
  CUSTOM: 'Upload Custom',
};

// Raw wire values for `DocumentSource` (GetPlanningResponse.documents[].source).
export const DOC_SOURCE_API = {
  MASTER: 'MasterDocument',
  CUSTOM: 'UploadCustom',
};

// Fixed Jenis Pengadaan / Mekanisme combination the Planning feature supports —
// the Create endpoint 422s anything else (contract section 1). Used to pick the
// right lookup rows and to label the read-only Procurement Type field.
export const SUPPORTED_JENIS_PENGADAAN_CODE = 'COS';
export const SUPPORTED_MEKANISME_NAME = 'Pelelangan khusus';

// Requirement tab inner section navigation.
export const REQUIREMENT_SECTIONS = [
  { id: 'contract', label: 'Contract' },
  { id: 'cargo', label: 'Cargo Specification' },
  { id: 'clause', label: 'Clause Detail' },
];

// Colour mapping for the Status Planning badge in the list.
export const STATUS_BADGE = {
  [STATUS.DRAFT]: 'warning',
  [STATUS.SUBMITTED]: 'success',
  [STATUS.HARD_RECALL]: 'danger',
};

// Colour mapping for the Main Status badge in the list.
export const MAIN_STATUS_BADGE = {
  [MAIN_STATUS.PLANNING]: 'info',
  [MAIN_STATUS.PROCUREMENT]: 'primary',
  [MAIN_STATUS.TENDER_OPENING]: 'secondary',
};

// Hard Recall filter options for the list (contract section 2, `isHardRecall`
// query param added 2026-07-23). `value` is sent on the wire as-is; `null` means
// "don't send the param" — the server then applies no filter.
export const HARD_RECALL_FILTERS = [
  { value: null, label: 'All Planning' },
  { value: true, label: 'Hard Recall only' },
  { value: false, label: 'Exclude Hard Recall' },
];
